import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Avatar } from '@mui/material';
import { Edit } from 'lucide-react';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RepricingHistoryModal from './RepricingHistoryModal';
import api from '../utils/api';

const mpTLD = {
  FR: 'fr', IT: 'it', ES: 'es', NL: 'nl', SE: 'se', PL: 'pl', BE: 'be', DE: 'de', UK: 'co.uk',
};

const RepricerProduit = ({
  produit,
  repricingRuleData,
  rules,
  exchangeRates,
  restrictionsByProduct,
  setSelectedProduct,
  setOpenRepricerModal,
  setAsinToRefresh,
  setOpenConfirmRefresh,
  purgeRestrictionsCacheForAsin,
  setRefreshRestrictionsTrigger,
  MARKETPLACES,
  repricingTrends
}) => {
  const [openHistory, setOpenHistory] = useState(false);
  const [historyRuleId, setHistoryRuleId] = useState(null);

  const handleOpenHistory = (mpCode) => {
    const rule = repricingRuleData[produit.id]?.[mpCode];
    if (rule && rule.id) {
      setHistoryRuleId(rule.id);
      setOpenHistory(true);
    }
  };

  const handleCloseHistory = () => {
    setOpenHistory(false);
    setHistoryRuleId(null);
  };

  return (
    <Box
      sx={{
        minWidth: 220,
        maxWidth: 340,
        bgcolor: '#f8fafc',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        borderRight: { md: '1px solid #e3e6ef' },
        gap: 1,
        position: 'relative',
        overflowY: 'hidden',
        boxSizing: 'border-box',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Repricer</Typography>
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedProduct(produit);
              setOpenRepricerModal(true);
            }}
            sx={{ mr: 0.5 }}
          >
            <Edit size={18} />
          </IconButton>
          <Tooltip title="Rafraîchir les restrictions (purge le cache)">
            <IconButton
              size="small"
              onClick={() => {
                setAsinToRefresh(produit.ASIN);
                setOpenConfirmRefresh(true);
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Repricer en version compacte, une ligne par marketplace */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
        {MARKETPLACES.map(mp => {
          const rule = repricingRuleData[produit.id]?.[mp.code];
          const isActive = !!rule?.active;
          const price = rule?.target_price;
          const currency = rule?.currency || mp.currencyCode || mp.currency;
          let display = '';
          if (["SEK", "PLN", "GBP"].includes(currency) && exchangeRates[currency] && price !== undefined && price !== null && !isNaN(price)) {
            display = (Number(price) * exchangeRates[currency]).toFixed(2) + ' €';
          } else if (price !== undefined && price !== null && !isNaN(price)) {
            const currencyDisplay = currency === 'EUR' ? '€' : currency;
            display = Number(price).toFixed(2) + ' ' + currencyDisplay;
          }
          const getBB = rule?.getBB;
          const position = rule?.Position;
          const bb = rule?.BB;
          // Restriction
          const restriction = restrictionsByProduct[produit.id]?.[mp.id];
          const tld = mpTLD[mp.code] || 'fr';
          const asin = produit.ASIN;
          const url = `https://sellercentral.amazon.${tld}/hz/approvalrequest/restrictions/approve?asin=${asin}&itemcondition=New`;
          const showLock = restriction && Array.isArray(restriction.reasons) && restriction.reasons.length > 0;

          // Tooltip sur le drapeau
          let min = '', max = '', minUnit = '', maxUnit = '';
          if (rule) {
            if (rule.min_profit_override !== null && rule.min_profit_override !== undefined) {
              min = rule.min_profit_override;
              minUnit = '€';
            } else if (rule.min_margin_override !== null && rule.min_margin_override !== undefined) {
              min = rule.min_margin_override;
              minUnit = '%';
            } else if (rule.min_roi_override !== null && rule.min_roi_override !== undefined) {
              min = rule.min_roi_override;
              minUnit = '%';
            }
            if (rule.max_profit_override !== null && rule.max_profit_override !== undefined) {
              max = rule.max_profit_override;
              maxUnit = '€';
            } else if (rule.max_margin_override !== null && rule.max_margin_override !== undefined) {
              max = rule.max_margin_override;
              maxUnit = '%';
            } else if (rule.max_roi_override !== null && rule.max_roi_override !== undefined) {
              max = rule.max_roi_override;
              maxUnit = '%';
            }
          }
          let drapeauTooltip = null;
          if (min !== '' || max !== '') {
            drapeauTooltip = (
              <div style={{fontSize: 13}}>
                Min : {min}{minUnit} {max !== '' ? `- Max : ${max}${maxUnit}` : ''}
              </div>
            );
          } else if (rule && rule.rule_id) {
            const ruleObj = rules.find(r => r.id === rule.rule_id);
            if (ruleObj && ruleObj.rule_name) {
              drapeauTooltip = <div>{ruleObj.rule_name}</div>;
            }
          } else {
            drapeauTooltip = null;
          }

          // Tooltip sur le prix (SE, PL, UK)
          let prixTooltip = '';
          if (["SEK", "PLN", "GBP"].includes(currency) && price !== undefined && price !== null && !isNaN(price)) {
            prixTooltip = Number(price).toFixed(2) + ' ' + currency;
          }

          let trendIcon = null;
          if (rule && rule.id && (repricingTrends?.[rule.id] === 'increase' || repricingTrends?.[rule.id] === 'decrease')) {
            if (repricingTrends[rule.id] === 'increase') {
              trendIcon = <ArrowDropUpIcon sx={{ color: 'success.main', fontSize: 22, verticalAlign: 'middle', ml: 0.5 }} titleAccess="Dernier changement : hausse" />;
            } else if (repricingTrends[rule.id] === 'decrease') {
              trendIcon = <ArrowDropDownIcon sx={{ color: 'error.main', fontSize: 22, verticalAlign: 'middle', ml: 0.5 }} titleAccess="Dernier changement : baisse" />;
            }
          }

          return (
            <Box key={mp.code} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 24, pl: 0.5, fontSize: '0.92em' }}>
              <Tooltip title={drapeauTooltip} arrow placement="top">
                <Box
                  sx={{
                    borderLeft: `2px solid ${isActive ? '#4caf50' : 'transparent'}`,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    pl: 0.5,
                    pr: 0.5,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleOpenHistory(mp.code)}
                >
                  <Avatar
                    src={mp.flag}
                    alt={mp.code}
                    sx={{ width: 18, height: 14, borderRadius: 1, border: '1px solid #eee' }}
                  />
                </Box>
              </Tooltip>
              {/* Affichage prix OU cadenas si restriction sans prix */}
              {(!display && showLock) ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                  tabIndex={-1}
                  onClick={e => {
                    e.stopPropagation();
                    if (asin) {
                      purgeRestrictionsCacheForAsin(asin);
                      setRefreshRestrictionsTrigger(t => t + 1);
                    }
                  }}
                >
                  <Tooltip
                    title={<div>{restriction.reasons.map((r, i) => (<div key={i}>{r.message}</div>))}</div>}
                    arrow
                  >
                    <LockIcon fontSize="small" color="warning" style={{ verticalAlign: 'middle', cursor: 'pointer' }} />
                  </Tooltip>
                </a>
              ) : (
                ["SEK", "PLN", "GBP"].includes(currency) && prixTooltip ? (
                  <Tooltip title={prixTooltip} arrow>
                    <Box sx={{ minWidth: 60, fontWeight: 400, color: display ? (isActive ? '#222' : '#888') : '#bbb', fontSize: '0.92em', display: 'flex', alignItems: 'center' }}>
                      {display || <span style={{ color: '#bbb', fontWeight: 400 }}>--</span>}
                      {trendIcon}
                    </Box>
                  </Tooltip>
                ) : (
                  <Box sx={{ minWidth: 60, fontWeight: 400, color: display ? (isActive ? '#222' : '#888') : '#bbb', fontSize: '0.92em', display: 'flex', alignItems: 'center' }}>
                    {display || <span style={{ color: '#bbb', fontWeight: 400 }}>--</span>}
                    {trendIcon}
                  </Box>
                )
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                {position !== undefined && position !== null && (
                  <Tooltip title={`Votre position : #${position}`} arrow>
                    <span style={{
                      display: 'inline-block',
                      background: position === 1 ? '#4caf50' : '#1976d2',
                      color: '#fff',
                      borderRadius: 3,
                      padding: '0 6px',
                      fontSize: '0.93em',
                      height: 20,
                      lineHeight: '20px',
                      fontWeight: 600,
                      minWidth: 28,
                      textAlign: 'center',
                    }}>{`#${position}`}</span>
                  </Tooltip>
                )}
                {bb !== undefined && bb !== null && (
                  <Tooltip title={`Prix Buybox : ${Number(bb).toFixed(2)} €`} arrow>
                    <span style={{
                      display: 'inline-block',
                      background: '#ff9800',
                      color: '#fff',
                      borderRadius: 3,
                      padding: '0 7px',
                      fontSize: '0.93em',
                      height: 20,
                      lineHeight: '20px',
                      fontWeight: 600,
                      minWidth: 38,
                      textAlign: 'center',
                    }}>{`BB: ${Number(bb).toFixed(2)} €`}</span>
                  </Tooltip>
                )}
                {getBB && (
                  <Tooltip title="Vous détenez la Buybox" arrow>
                    <span style={{fontSize:'1.1em',marginRight:2}}>🏆</span>
                  </Tooltip>
                )}
                {/* Si prix ET restriction, cadenas à droite */}
                {display && showLock && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                    tabIndex={-1}
                    onClick={e => {
                      e.stopPropagation();
                      if (asin) {
                        purgeRestrictionsCacheForAsin(asin);
                        setRefreshRestrictionsTrigger(t => t + 1);
                      }
                    }}
                  >
                    <Tooltip
                      title={<div>{restriction.reasons.map((r, i) => (<div key={i}>{r.message}</div>))}</div>}
                      arrow
                    >
                      <LockIcon fontSize="small" color="warning" style={{ marginLeft: 2, verticalAlign: 'middle', cursor: 'pointer' }} />
                    </Tooltip>
                  </a>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      <RepricingHistoryModal
        open={openHistory}
        onClose={handleCloseHistory}
        repricingRulesId={historyRuleId}
      />
    </Box>
  );
};

export default RepricerProduit; 